<template>
    <v-container fluid class="login-page">
        <div class="login-shell">
            <div class="login-brand-panel">
                <v-img class="brand-logo" :src="nttDataLogo" max-height="40" min-width="200" contain></v-img>

                <div class="brand-content">
                    <h1 class="brand-title">
                        {{ t('authentication.socialSimulationPlatform') }}
                    </h1>

                    <p class="brand-subtitle">
                        {{ t('authentication.aipoweredFocusGroupFacilitationToUnlockDeeperInsights') }}
                    </p>
                </div>

                <div class="brand-waves" aria-hidden="true">
                    <svg class="wave-svg wave-svg-one" viewBox="0 0 520 260" preserveAspectRatio="none">
                        <path v-for="i in 18" :key="`wave-one-${i}`" :d="getWavePath(i, 0)"
                            class="wave-line wave-line-primary" />
                    </svg>

                    <svg class="wave-svg wave-svg-two" viewBox="0 0 520 260" preserveAspectRatio="none">
                        <path v-for="i in 16" :key="`wave-two-${i}`" :d="getWavePath(i, 34)"
                            class="wave-line wave-line-secondary" />
                    </svg>
                </div>
            </div>

            <div class="login-form-panel">
                <v-card class="login-card" elevation="6">
                    <div class="w-100 d-flex justify-space-between mb-2">
                        <div class="d-flex flex-column">
                            <v-card-title class="text-h5 font-weight-bold mb-2 pa-0">
                                {{ t('authentication.login') }}
                            </v-card-title>

                            <v-card-subtitle class="mb-6 pa-0">
                                {{ t('authentication.accessSocialSimulation') }}
                            </v-card-subtitle>
                        </div>
                        <lenguage-selector v-model="currentLanguage" mode="light" @change="handleLanguageChange" />
                    </div>


                    <v-alert v-if="errorMessage" type="error" variant="tonal" class="mb-4">
                        {{ errorMessage }}
                    </v-alert>

                    <v-form ref="formRef" :key="formKey" v-model="isFormValid" @submit.prevent="handleLogin">
                        <v-text-field v-model="form.email" :label="$t('authentication.email')" type="email"
                            prepend-inner-icon="mdi-email-outline" variant="outlined" density="comfortable"
                            autocomplete="email" :rules="[rules.required, rules.email]" class="mb-3" />

                        <v-text-field v-model="form.password" :type="showPassword ? 'text' : 'password'"
                            :label="$t('authentication.password')" prepend-inner-icon="mdi-lock-outline"
                            :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'" variant="outlined"
                            density="comfortable" autocomplete="current-password" :rules="[rules.required]" class="mb-4"
                            @click:append-inner="showPassword = !showPassword" />

                        <v-btn type="submit" color="primary" block size="large" :loading="loading"
                            :disabled="!isFormValid || loading">
                            {{ t('authentication.login') }}
                        </v-btn>
                    </v-form>

                    <!-- <div class="mt-5 text-center">
                        <span class="text-body-2">
                            {{ t('authentication.dontHaveAnAccount') }}
                        </span>

                        <v-btn variant="text" color="primary" class="ml-1" @click="goToRegistration">
                            {{ t('authentication.register') }}
                        </v-btn>
                    </div> -->
                </v-card>
            </div>
        </div>
    </v-container>
</template>

<script>
import LenguageSelector from "./LenguageSelector.vue";
import nttDataLogo from '@/assets/ntt_data_logo.png';
import { login_user, set_auth_session } from "@/api";
import { useI18n } from "vue-i18n";


export default {
    name: "LoginPage",
    components: {
        LenguageSelector,
    },
    setup() {
        const { t } = useI18n();

        return {
            t
        };
    },
    data() {
        return {
            currentLanguage: localStorage.getItem('appLanguage') || 'en',
            nttDataLogo: nttDataLogo,
            formKey: 0,
            form: {
                email: "",
                password: "",
            },
            isFormValid: false,
            loading: false,
            errorMessage: "",
            showPassword: false,
            rules: {
                required: value => !!value || this.$t('authentication.fieldIsRequired'),
                email: value => {
                    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    return pattern.test(value) || this.$t('authentication.invalidEmail');
                },
            },
        };
    },

    methods: {
        async handleLogin() {
            this.errorMessage = "";

            const validation = await this.$refs.formRef.validate();

            if (!validation.valid) {
                return;
            }

            this.loading = true;

            try {
                const response = await login_user({
                    email: this.form.email,
                    password: this.form.password,
                });

                const user = set_auth_session(response);

                if (user?.is_admin) {
                    this.$router.push("/persona");
                    return;
                }

                this.$router.push("/persona");
            } catch (error) {
                this.errorMessage = this.$t('authentication.loginFailedPleaseCheckYourCredentials');
            } finally {
                this.loading = false;
            }
        },

        handleLanguageChange(languageCode) {
            this.currentLanguage = languageCode
            this.$i18n.locale = languageCode

            localStorage.setItem('appLanguage', languageCode)

            this.resetFormValidation()
        },

        resetFormValidation() {
            this.errorMessage = '';
            this.isFormValid = false;
            this.showPassword = false;

            this.formKey++;
        },

        getErrorMessage(error) {
            const detail = error?.response?.data?.detail;

            if (typeof detail === "string") {
                return detail;
            }

            if (Array.isArray(detail)) {
                return detail
                    .map(item => item.msg)
                    .join(", ");
            }

            if (detail?.message) {
                return detail.message;
            }

            return this.$t('authentication.loginFailedPleaseCheckYourCredentials');
        },

        getWavePath(index, offset = 0) {
            const y = 72 + index * 7 + offset;

            return `
                    M -40 ${y}
                    C 70 ${y - 56}, 150 ${y + 58}, 260 ${y}
                    S 430 ${y - 54}, 560 ${y + 10}
                `;
        },

    },
    computed: {
        rules() {
            return {
                required: value =>
                    !!value || this.$t('authentication.fieldIsRequired'),

                email: value => {
                    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                    return (
                        pattern.test(value)
                        || this.$t('authentication.invalidEmail')
                    );
                },
            };
        },
    },

};
</script>

<style scoped>
.v-container {
    padding: 0px;
}

.login-page {
    height: 100%;
    background: linear-gradient(135deg, #f5f7fb 0%, #e8eef7 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow-y: auto;
}

.login-shell {
    width: 100%;
    display: grid;
    grid-template-columns: 360px 1fr;
    background: transparent;
    overflow: hidden;
    height: 100%;
}

.login-brand-panel {
    position: relative;
    overflow: hidden;
    padding: 32px;
    color: white;
    background:
        radial-gradient(circle at 20% 10%, rgba(45, 107, 255, 0.35), transparent 28%),
        radial-gradient(circle at 90% 70%, rgba(0, 105, 255, 0.28), transparent 34%),
        linear-gradient(160deg, #061736 0%, #020b20 52%, #001a4d 100%);
}

.brand-content {
    position: relative;
    z-index: 2;
    margin-top: 180px;
}

.brand-logo {
    transform: translateX(-35px);
    width: fit-content;
}

.brand-title {
    font-size: 30px;
    line-height: 1.08;
    font-weight: 800;
    margin: 0 0 18px;
    letter-spacing: -0.6px;
}

.brand-subtitle {
    font-size: 13px;
    line-height: 1.55;
    color: rgba(255, 255, 255, 0.72);
    margin: 0;
}

.wave {
    position: absolute;
    left: -18%;
    width: 140%;
    height: 220px;
    border-radius: 50%;
    opacity: 0.75;
    transform: rotate(-8deg);
    background:
        repeating-radial-gradient(ellipse at center,
            rgba(47, 121, 255, 0.42) 0,
            rgba(47, 121, 255, 0.42) 1px,
            transparent 2px,
            transparent 7px);
    filter: blur(0.2px);
}

.brand-waves {
    position: absolute;
    left: -8%;
    right: -8%;
    bottom: -10px;
    height: 310px;
    pointer-events: none;
    z-index: 1;
    overflow: hidden;
}

.wave-svg {
    position: absolute;
    left: 0;
    width: 116%;
    height: 230px;
    overflow: visible;
}

.wave-svg-one {
    bottom: 86px;
    transform: rotate(1deg);
    opacity: 0.95;
}

.wave-svg-two {
    bottom: 8px;
    transform: rotate(-2deg);
    opacity: 0.75;
}

.wave-line {
    fill: none;
    stroke-linecap: round;
    vector-effect: non-scaling-stroke;
}

.wave-line-primary {
    stroke: rgba(37, 118, 255, 0.55);
    stroke-width: 1.1;
}

.wave-line-secondary {
    stroke: rgba(0, 83, 210, 0.48);
    stroke-width: 0.95;
}

.brand-waves::before {
    content: "";
    position: absolute;
    left: 10%;
    right: 8%;
    bottom: 96px;
    height: 90px;
    background: radial-gradient(ellipse at center,
            rgba(0, 119, 255, 0.28) 0%,
            rgba(0, 119, 255, 0.12) 38%,
            transparent 72%);
    filter: blur(18px);
}

.brand-waves::after {
    content: "";
    position: absolute;
    left: -10%;
    right: -10%;
    bottom: -28px;
    height: 150px;
    background: linear-gradient(180deg,
            transparent 0%,
            rgba(0, 30, 95, 0.35) 46%,
            rgba(0, 12, 42, 0.85) 100%);
}

.login-form-panel {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px;
    background: rgba(255, 255, 255, 0.72);
    backdrop-filter: blur(10px);
}

.login-card {
    width: 100%;
    max-width: 430px;
    border-radius: 20px;
    padding: 28px;
}
</style>